/**
 * Super-Admin — Letterhead Templates management
 *
 * • Lists all templates (system + custom) from GET /api/admin/letterhead-templates
 * • New template flow: metadata modal → opens the studio in "template mode"
 * • Edit flow: opens the studio in template mode with the template's preset loaded
 * • Duplicate: server-side clone; then opens the duplicate for editing
 * • Delete: only for non-system templates (backend enforces)
 *
 * The design canvas is the same `LetterheadStudio` labs use, just with
 * `templateMode` on. This gives super admins the exact same "what you see
 * is what labs get" preview.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Copy, Trash2, LayoutTemplate, ChevronLeft, Save as SaveIcon, X as XIcon } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import { DashboardLayout } from "@/components/Layout";
import { api, apiError } from "@/lib/api";
import { LetterheadStudio } from "@/pages/lab/LetterheadStudio";

/* ---------- List view ---------- */
function TemplatesList({ items, loading, onNew, onEdit, onDuplicate, onDelete }) {
  return (
    <div className="space-y-4" data-testid="admin-letterhead-templates-list">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading font-bold text-3xl text-ink flex items-center gap-2">
            <LayoutTemplate className="w-6 h-6 text-efgh-orange" /> Letterhead templates
          </h1>
          <p className="text-ink/60">
            One-click styles that every lab can apply from the Letterhead Studio. System templates cannot be deleted, but you can edit or duplicate them.
          </p>
        </div>
        <Button onClick={onNew} data-testid="admin-tpl-new" className="bg-efgh-orange hover:bg-efgh-orange/90 text-white">
          <Plus className="w-4 h-4 mr-1.5" /> New template
        </Button>
      </div>

      {loading ? (
        <Card className="p-10 text-center text-ink/50 rounded-2xl">Loading templates…</Card>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center text-ink/50 rounded-2xl">No templates yet. Click &quot;New template&quot; to create one.</Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((t) => (
            <Card key={t.id} className="p-4 rounded-2xl hover:shadow-md transition-shadow" data-testid={`admin-tpl-card-${t.id}`}>
              <div className="flex items-start justify-between gap-2 mb-1">
                <h3 className="font-heading font-semibold text-ink text-lg">{t.name}</h3>
                <div className="flex gap-1 shrink-0">
                  {(t.swatches || []).map((s, i) => (
                    <span key={i} className="w-5 h-5 rounded-full border border-border shadow-sm" style={{ background: s }} />
                  ))}
                </div>
              </div>
              <p className="text-xs text-ink/60 leading-snug min-h-[32px]">{t.tagline || <em className="text-ink/30">no tagline</em>}</p>
              <div className="mt-2 flex items-center gap-1.5">
                <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${t.is_system ? "bg-efgh-blue/10 text-efgh-blue" : "bg-efgh-green/10 text-efgh-green"}`}>
                  {t.is_system ? "System" : "Custom"}
                </span>
                <span className="text-[9px] text-ink/40">·  {Object.keys(t.preset || {}).length} preset keys</span>
              </div>
              <div className="mt-3 pt-3 border-t border-border flex items-center gap-1.5 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => onEdit(t)} data-testid={`admin-tpl-edit-${t.id}`} className="h-7 text-xs">
                  <Pencil className="w-3 h-3 mr-1" /> Edit
                </Button>
                <Button size="sm" variant="outline" onClick={() => onDuplicate(t)} data-testid={`admin-tpl-duplicate-${t.id}`} className="h-7 text-xs">
                  <Copy className="w-3 h-3 mr-1" /> Duplicate
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onDelete(t)}
                  disabled={t.is_system}
                  data-testid={`admin-tpl-delete-${t.id}`}
                  title={t.is_system ? "System templates can't be deleted" : "Delete"}
                  className="h-7 text-xs text-danger hover:text-danger disabled:opacity-30"
                >
                  <Trash2 className="w-3 h-3 mr-1" /> Delete
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Metadata editor (name / tagline / swatches) ---------- */
function TemplateMetadata({ meta, setMeta }) {
  const updateSwatch = (i, val) => {
    const next = [...(meta.swatches || [])];
    while (next.length <= i) next.push("");
    next[i] = val;
    setMeta((m) => ({ ...m, swatches: next }));
  };
  return (
    <Card className="p-4 rounded-2xl" data-testid="admin-tpl-metadata">
      <div className="grid md:grid-cols-[1fr_1fr_auto] gap-4 items-start">
        <div>
          <Label className="text-xs font-medium text-ink">Template name</Label>
          <Input
            value={meta.name}
            onChange={(e) => setMeta((m) => ({ ...m, name: e.target.value }))}
            data-testid="admin-tpl-name"
            placeholder="e.g. Corporate Blue"
            maxLength={60}
          />
        </div>
        <div>
          <Label className="text-xs font-medium text-ink">Tagline</Label>
          <Input
            value={meta.tagline}
            onChange={(e) => setMeta((m) => ({ ...m, tagline: e.target.value }))}
            data-testid="admin-tpl-tagline"
            placeholder="Short one-line description shown to labs"
            maxLength={200}
          />
        </div>
        <div>
          <Label className="text-xs font-medium text-ink block">Preview swatches</Label>
          <div className="flex gap-1.5 mt-1.5">
            {[0, 1, 2].map((i) => {
              const val = (meta.swatches || [])[i] || "#";
              return (
                <input
                  key={i}
                  type="color"
                  value={/^#[0-9a-f]{6}$/i.test(val) ? val : "#000000"}
                  onChange={(e) => updateSwatch(i, e.target.value)}
                  data-testid={`admin-tpl-swatch-${i}`}
                  className="w-9 h-9 border border-border rounded-lg cursor-pointer bg-white p-0.5"
                  title={`Swatch ${i + 1}`}
                />
              );
            })}
          </div>
        </div>
      </div>
    </Card>
  );
}

/* ---------- Editor view — LetterheadStudio in "template mode" ---------- */
function TemplateEditor({ template, onCancel, onSaved }) {
  // `form` mirrors the shape a lab's form would have so LetterheadStudio can
  // read it without special-casing. The interesting field is `letterhead`.
  const [form, setForm] = useState({
    letterhead: template.preset || {},
    // Empty lists so the (hidden) Assets group renders cleanly.
    accreditation_logos: [],
    custom_logos: [],
    doctor_signatures: [],
  });
  const [meta, setMeta] = useState({
    name: template.name || "",
    tagline: template.tagline || "",
    swatches: [...(template.swatches || []), "", "", ""].slice(0, 3),
  });
  const [saving, setSaving] = useState(false);

  const dummyLab = { id: "template-editor", name: meta.name || "Sample Lab", branding: { primary_color: "#0B7A4D" } };

  const doSave = useCallback(async () => {
    if (!meta.name.trim()) { toast.error("Give the template a name first"); return; }
    setSaving(true);
    try {
      const body = {
        name: meta.name.trim(),
        tagline: meta.tagline.trim(),
        swatches: (meta.swatches || []).filter((s) => /^#[0-9a-f]{3}([0-9a-f]{3})?$/i.test(s || "")),
        preset: form.letterhead || {},
      };
      const isNew = !template.id;
      const r = isNew
        ? await api.post("/admin/letterhead-templates", body)
        : await api.put(`/admin/letterhead-templates/${template.id}`, body);
      toast.success(`Template ${isNew ? "created" : "saved"}`);
      onSaved(r.data);
    } catch (e) { toast.error(apiError(e, "Save failed")); }
    finally { setSaving(false); }
  }, [meta, form.letterhead, template.id, onSaved]);

  return (
    <div className="space-y-4" data-testid="admin-tpl-editor">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <button onClick={onCancel} className="p-1.5 rounded hover:bg-white border border-border" data-testid="admin-tpl-back">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <h1 className="font-heading font-bold text-2xl text-ink">
            {template.id ? "Edit template" : "New template"}
            {template.is_system && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-efgh-blue/10 text-efgh-blue align-middle">System</span>}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={onCancel} data-testid="admin-tpl-cancel">
            <XIcon className="w-4 h-4 mr-1" /> Cancel
          </Button>
          <Button onClick={doSave} disabled={saving} data-testid="admin-tpl-save" className="bg-efgh-green hover:bg-efgh-green/90 text-white">
            <SaveIcon className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save template"}
          </Button>
        </div>
      </div>

      <TemplateMetadata meta={meta} setMeta={setMeta} />

      <LetterheadStudio
        lab={dummyLab}
        form={form}
        setForm={setForm}
        save={doSave}
        templateMode
      />
    </div>
  );
}

/* ---------- Root ---------- */
export default function AdminLetterheadTemplates() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {id?, name, tagline, swatches, preset}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/letterhead-templates");
      setItems(r.data || []);
    } catch (e) { toast.error(apiError(e, "Failed to load templates")); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onNew = () => setEditing({ name: "", tagline: "", swatches: [], preset: {}, is_system: false });
  const onEdit = (t) => setEditing({ ...t });
  const onDuplicate = async (t) => {
    try {
      const r = await api.post(`/admin/letterhead-templates/${t.id}/duplicate`);
      toast.success("Duplicated — opening for edit");
      setEditing(r.data);
      load();
    } catch (e) { toast.error(apiError(e, "Duplicate failed")); }
  };
  const onDelete = async (t) => {
    if (t.is_system) return;
    if (!window.confirm(`Delete template "${t.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/letterhead-templates/${t.id}`);
      toast.success("Template deleted");
      load();
    } catch (e) { toast.error(apiError(e, "Delete failed")); }
  };
  const onSaved = () => { setEditing(null); load(); };
  const onCancel = () => setEditing(null);

  return (
    <DashboardLayout role="super_admin">
      {editing ? (
        <TemplateEditor template={editing} onCancel={onCancel} onSaved={onSaved} />
      ) : (
        <TemplatesList items={items} loading={loading} onNew={onNew} onEdit={onEdit} onDuplicate={onDuplicate} onDelete={onDelete} />
      )}
    </DashboardLayout>
  );
}
